/**
 * CoPilotHistory — the shared ChatGPT-style conversation-history panel (0089).
 *
 * Rendered IN-PLACE inside the 384px CoPilot sidebar (both portals): a history
 * icon in the conversation toolbar flips the panel from the live chat to this
 * searchable list; picking a row flips back into that conversation. Minimal UI
 * footprint, ChatGPT feel.
 *
 * Fully data-driven off the SDK conversation hooks; the host injects only the
 * engine actions (select / new / back) so the list can drive the one hoisted
 * Companion engine.
 */

import { useLocale, useTranslation } from '@tonnie/locale';
import { useCoPilotConversations, useDeleteConversation, useRenameConversation } from '@tonnie/sdk';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  ScrollArea,
  Skeleton,
} from '@tonnie/ui';
import { ArrowLeft, MoreVertical, Pencil, Search, SquarePen, Trash2 } from 'lucide-react';
import { useState } from 'react';

export interface ICoPilotHistoryProps {
  /** The open conversation — its row is highlighted. */
  currentConversationId: string | null;
  /** Open a past conversation and continue it. */
  onSelect: (id: string) => void;
  /** Start a fresh chat (client-only; no row until the first message). */
  onNewConversation: () => void;
  /** Return to the live chat view without changing conversation. */
  onBack: () => void;
}

/** Compact relative time ("2h", "3d", "5w") in the app locale. */
function useRelativeTime(): (iso: string | null) => string {
  const { locale } = useLocale();
  return (iso) => {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    const diffMs = Date.now() - then;
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' });
    const min = Math.round(diffMs / 60000);
    if (min < 60) return rtf.format(-Math.max(min, 0), 'minute');
    const hr = Math.round(min / 60);
    if (hr < 24) return rtf.format(-hr, 'hour');
    const day = Math.round(hr / 24);
    if (day < 7) return rtf.format(-day, 'day');
    const wk = Math.round(day / 7);
    if (wk < 5) return rtf.format(-wk, 'week');
    const mo = Math.round(day / 30);
    if (mo < 12) return rtf.format(-mo, 'month');
    return rtf.format(-Math.round(day / 365), 'year');
  };
}

export function CoPilotHistory({
  currentConversationId,
  onSelect,
  onNewConversation,
  onBack,
}: ICoPilotHistoryProps) {
  const { t } = useTranslation();
  const relTime = useRelativeTime();
  const [query, setQuery] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameText, setRenameText] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data, isLoading } = useCoPilotConversations({ query: query.trim() || undefined });
  const renameMutation = useRenameConversation();
  const deleteMutation = useDeleteConversation();

  const conversations = data?.conversations ?? [];

  const submitRename = (id: string) => {
    const title = renameText.trim();
    if (title) renameMutation.mutate({ id, title });
    setRenamingId(null);
  };

  const confirmDelete = () => {
    if (deletingId) deleteMutation.mutate({ id: deletingId });
    setDeletingId(null);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden min-h-0" data-testid="copilot-history">
      {/* Header: back · title · new chat */}
      <div className="flex items-center justify-between gap-2 px-4 pt-3 pb-1">
        <button
          type="button"
          onClick={onBack}
          data-testid="copilot-history-back"
          className="flex items-center gap-1.5 rounded-full px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          {t('copilot.history.back')}
        </button>
        <button
          type="button"
          onClick={onNewConversation}
          data-testid="copilot-history-new"
          className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <SquarePen className="h-3.5 w-3.5" aria-hidden="true" />
          {t('copilot.history.newChat')}
        </button>
      </div>

      {/* Search */}
      <div className="px-4 py-2">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('copilot.history.searchPlaceholder')}
            aria-label={t('copilot.history.searchPlaceholder')}
            data-testid="copilot-history-search"
            className="h-9 pl-8 text-sm"
          />
        </div>
      </div>

      {/* List */}
      <ScrollArea className="flex-1 min-h-0">
        <ul className="px-2 pb-4" data-testid="copilot-history-list">
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <li key={i} className="px-2 py-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="mt-1.5 h-3 w-1/3" />
              </li>
            ))
          ) : conversations.length === 0 ? (
            <li
              className="px-4 py-10 text-center text-sm text-muted-foreground"
              data-testid="copilot-history-empty"
            >
              {query.trim() ? t('copilot.history.emptySearch') : t('copilot.history.empty')}
            </li>
          ) : (
            conversations.map((c) => {
              const isActive = c.id === currentConversationId;
              const isRenaming = c.id === renamingId;
              return (
                <li key={c.id}>
                  <div
                    className={cn(
                      'group flex items-center gap-2 rounded-lg px-2 py-2 transition-colors',
                      isActive ? 'bg-secondary' : 'hover:bg-secondary/60'
                    )}
                  >
                    {isRenaming ? (
                      <Input
                        autoFocus
                        value={renameText}
                        onChange={(e) => setRenameText(e.target.value)}
                        onBlur={() => submitRename(c.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') submitRename(c.id);
                          if (e.key === 'Escape') setRenamingId(null);
                        }}
                        aria-label={t('copilot.history.rename')}
                        data-testid={`copilot-history-rename-input-${c.id}`}
                        className="h-8 flex-1 text-sm"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => onSelect(c.id)}
                        data-testid={`copilot-history-item-${c.id}`}
                        className="flex-1 min-w-0 text-left"
                      >
                        <p className="truncate text-sm text-sidebar-foreground">
                          {c.title || t('copilot.history.untitled')}
                        </p>
                        <p className="text-2xs text-muted-foreground">
                          {relTime(c.lastMessageAt)}
                          {c.hero ? ` · ${c.hero}` : ''}
                        </p>
                      </button>
                    )}

                    {!isRenaming && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={t('copilot.history.actions')}
                            data-testid={`copilot-history-item-menu-${c.id}`}
                            className="size-7 shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
                          >
                            <MoreVertical className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            data-testid={`copilot-history-rename-${c.id}`}
                            onSelect={() => {
                              setRenamingId(c.id);
                              setRenameText(c.title || '');
                            }}
                          >
                            <Pencil className="mr-2 h-4 w-4" aria-hidden="true" />
                            {t('copilot.history.rename')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            data-testid={`copilot-history-delete-${c.id}`}
                            onSelect={() => setDeletingId(c.id)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                            {t('copilot.history.delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </li>
              );
            })
          )}
        </ul>
      </ScrollArea>

      {/* Soft-delete confirm */}
      <AlertDialog open={!!deletingId} onOpenChange={(open) => !open && setDeletingId(null)}>
        <AlertDialogContent data-testid="copilot-history-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('copilot.history.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('copilot.history.deleteBody')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('copilot.history.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="copilot-history-delete-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('copilot.history.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
